<template>
  <div class="column content-box closing-reason-modal" @click="onContentClick">
    <woot-modal-header
      :header-title="$t('CLOSING_REASONS.WARNING')"
      :header-content="headerDescription"
    />

    <woot-loading-state
      v-if="inboxReasonsUiFlags.isFetching"
      :message="$t('CLOSING_REASONS.LIST.LOADING_MESSAGE')"
    />

    <div
      v-else-if="getInboxOfClosingReasons.length === 0"
      class="alert-filter flex-center"
    >
      <div class="text-center">
        <fluent-icon icon="warning" size="40" />
      </div>

      <div>
        <p v-html="$t('CLOSING_REASONS.CREATE.NOT_FOUND')" />
      </div>
    </div>

    <form v-else class="row" @submit.prevent="submit">
      <div class="medium-12 columns">
        <label :class="{ error: $v.selectedReason.$error }">
          {{ reasonLabel }}

          <select v-model="selectedReason">
            <option
              v-for="reason in getInboxOfClosingReasons"
              :key="reason.name"
              :value="reason.id"
            >
              {{ reason.name }}
            </option>
          </select>

          <span v-if="$v.selectedReason.$error" class="message">
            {{ $t('CLOSING_REASONS.CREATE.REASON.ERROR') }}
          </span>
        </label>

        <label>
          {{ $t('CLOSING_REASONS.CREATE.COMMENT.LABEL') }}

          <textarea
            v-model="comment"
            rows="2"
            :placeholder="$t('CLOSING_REASONS.CREATE.COMMENT.PLACEHOLDER')"
          />
        </label>

        <div
          v-if="Number(inbox.link_products_to_closing_reason)"
          class="sidebar-products-wrap"
        >
          <label>
            {{ $t('CONTACT_PANEL.PRODUCTS.PRODUCT_SELECT.TITLE') }}
          </label>

          <div
            ref="productWrap"
            class="product-wrap"
            :class="{
              'product-wrap--expanded': showSearchDropdownProductLabel,
            }"
            @keyup.esc="closeDropdownProductLabel"
          >
            <div @click.stop.prevent>
              <add-product-label @add="toggleProductLabels" />
            </div>

            <woot-label
              v-for="productLabel in selectedProductLabels"
              :key="productLabel.id"
              :title="productLabel.title"
              :description="productLabel.description"
              :show-close="true"
              :color="productLabel.color"
              variant="smooth"
              @click="removeProductLabel(productLabel.title)"
            />

            <div class="dropdown-wrap">
              <div
                :class="{
                  'dropdown-pane--open': showSearchDropdownProductLabel,
                }"
                class="dropdown-pane"
                @click.stop.prevent
              >
                <product-label-dropdown
                  v-if="showSearchDropdownProductLabel"
                  :account-product-labels="accountProductLabels"
                  :selected-product-labels="selectedProductLabelTitles"
                  @add="addProductLabel"
                  @remove="removeProductLabel"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <woot-button variant="clear" @click.prevent="onCancel">
          {{ $t('CLOSING_REASONS.CREATE.CANCEL') }}
        </woot-button>
        <woot-button :is-loading="uiFlags.isCreating">
          {{ $t('CLOSING_REASONS.ADD.FORM.SUBMIT') }}
        </woot-button>
      </div>
    </form>
  </div>
</template>

<script>
import alertMixin from 'shared/mixins/alertMixin';
import wootConstants from 'dashboard/constants/globals';
import { mapGetters, mapActions } from 'vuex';
import { required } from 'vuelidate/lib/validators';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import ProductLabelDropdown from 'shared/components/ui/productLabel/ProductLabelDropdown';
import AddProductLabel from 'shared/components/ui/dropdown/AddProductLabel';

export default {
  components: {
    ProductLabelDropdown,
    AddProductLabel,
  },
  mixins: [alertMixin],
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
    currentChat: {
      type: Object,
      default: () => ({}),
    },
    options: {
      type: Object,
      default: () => ({}),
    },
    redirectAfterResolve: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedReason: null,
      comment: null,
      selectedProductLabels: [],
      showSearchDropdownProductLabel: false,
    };
  },
  validations() {
    return {
      selectedReason: { required },
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'closingReasons/getUIFlags',
      inboxReasonsUiFlags: 'inboxClosingReasons/getUIFlags',

      getInboxOfClosingReasonsRaw:
        'inboxClosingReasons/getInboxOfClosingReasons',

      accountProductLabels: 'productLabels/getProductLabels',
    }),
    getInboxOfClosingReasons() {
      return [...this.getInboxOfClosingReasonsRaw].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, {
          numeric: true,
          sensitivity: 'base',
        })
      );
    },
    selectedProductLabelTitles() {
      return this.selectedProductLabels.map(item => item.title);
    },
    isSnoozing() {
      return Object.keys(this.options).length > 0;
    },
    headerDescription() {
      return this.isSnoozing
        ? this.$t('CLOSING_REASONS.CREATE.DESCRIPTION_SNOOZE')
        : this.$t('CLOSING_REASONS.CREATE.DESCRIPTION_RESOLVE');
    },
    reasonLabel() {
      return this.isSnoozing
        ? this.$t('CLOSING_REASONS.CREATE.REASON.LABEL_SNOOZE')
        : this.$t('CLOSING_REASONS.CREATE.REASON.LABEL_RESOLVE');
    },
  },
  methods: {
    ...mapActions('inboxClosingReasons', ['fetch']),
    ...mapActions('historyClosingReasons', ['create']),
    ...mapActions('productLabels', {
      fetchProductLabels: 'get',
    }),
    toggleProductLabels() {
      this.showSearchDropdownProductLabel = !this
        .showSearchDropdownProductLabel;
    },
    closeDropdownProductLabel() {
      this.showSearchDropdownProductLabel = false;
    },
    onContentClick(event) {
      if (
        this.showSearchDropdownProductLabel &&
        this.$refs.productWrap &&
        !this.$refs.productWrap.contains(event.target)
      ) {
        this.closeDropdownProductLabel();
      }
    },
    addProductLabel(productLabel) {
      const alreadyExists = this.selectedProductLabels.find(
        item => item.id === productLabel.id
      );

      if (alreadyExists) {
        return;
      }

      this.selectedProductLabels.push(productLabel);
    },
    removeProductLabel(productLabelTitle) {
      this.selectedProductLabels = this.selectedProductLabels.filter(
        item => item.title !== productLabelTitle
      );
    },
    onCancel() {
      this.$emit('on-close');
    },
    async submit() {
      try {
        this.$v.$touch();

        if (this.$v.$invalid) {
          return;
        }

        const statusChanged = await this.toggleStatus();
        if (!statusChanged) {
          return;
        }

        const data = {
          history_closing_reasons: {
            conversation_id: this.currentChat.id,
            closing_reason_id: this.selectedReason,
            comments: this.comment,
            products: this.selectedProductLabels.map(
              productLabel => productLabel.title
            ),
          },
        };

        this.create(data);

        this.$emit('on-close');
        this.$root.$emit('refresh_count_conversation', true);

        if (this.redirectAfterResolve) {
          this.$router.push(
            `/app/accounts/${this.$route.params.accountId}/dashboard`
          );
        }
      } catch (error) {
        this.showAlert(this.$t('CLOSING_REASONS.ADD.API.ERROR_MESSAGE'));
      }
    },
    toggleStatus() {
      const { SNOOZED, RESOLVED } = wootConstants.STATUS_TYPE;

      const status = this.isSnoozing ? SNOOZED : RESOLVED;
      const snooze = this.isSnoozing ? this.options.snooze : undefined;

      return this.$store
        .dispatch('toggleStatus', {
          conversationId: this.currentChat.id,
          status: status,
          snoozedUntil: snooze,
        })
        .then(() => {
          this.showAlert(this.$t('CONVERSATION.CHANGE_STATUS'));
          return true;
        })
        .catch(error => {
          this.showAlert(
            parseAPIErrorResponse(error) ||
              this.$t('CONVERSATION.CHANGE_STATUS_FAILED')
          );
          return false;
        });
    },
    async getData() {
      try {
        if (!this.getInboxOfClosingReasons.length) {
          await this.fetch(this.inbox.id);
        }

        if (Number(this.inbox.link_products_to_closing_reason)) {
          await this.fetchProductLabels();
        }
      } catch (error) {
        this.showAlert(this.$t('CLOSING_REASONS.ADD.API.ERROR_MESSAGE'));
      }
    },
  },
  mounted() {
    this.getData();
  },
};
</script>

<style lang="scss" scoped>
.alert-filter {
  padding: var(--space-small);
  margin: var(--space-large) var(--space-large);
  background: var(--r-50);
  border-radius: var(--border-radius-small);

  div:first-of-type {
    flex: 15 1;

    svg {
      color: var(--r-800);
    }
  }

  div:last-child {
    flex: 75 1;

    p {
      font-size: var(--font-size-mini);
      font-weight: var(--font-weight-medium);
      color: var(--b-900);
      line-height: 1.5;
    }
  }
}

.sidebar-products-wrap {
  margin-top: var(--space-medium);

  .product-wrap {
    line-height: var(--space-medium);
    position: relative;

    &.product-wrap--expanded {
      padding-bottom: 16rem;
    }

    .dropdown-wrap {
      display: flex;
      left: 0;
      position: absolute;
      top: calc(var(--space-large) + var(--space-small));
      width: 26rem;
      z-index: 10;

      .dropdown-pane {
        width: 100%;
        box-sizing: border-box;
        background: var(--white);
        border-radius: var(--border-radius-normal);
        box-shadow: var(--shadow-large);
      }
    }
  }
}

.modal-footer {
  width: 100%;
  justify-content: end !important;
}
</style>
